import type { Address, Business, OnboardingApplication, OnboardingData, Person } from "@payezy/api-client/public";
import {
  getMeQueryKey,
  getOnboardingOptions,
  getOnboardingQueryKey,
  saveOnboardingMutation,
  submitOnboardingDocumentsMutation,
  submitOnboardingMutation,
  uploadOnboardingDocumentMutation,
} from "@payezy/api-client/public/query";
import { Alert, Badge, Button, Card, CardBody, CardHeader, Field, Input, PageHeader, Select, Skeleton, Table, TD, TH, THead, TR, formatDate, humanize, toast } from "@payezy/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckCircle2, FileUp, Plus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { isProblem, problemMessage } from "../lib/api";
import { useOrg } from "../lib/org";

type Errors = Record<string, string>;

const STATES =
  "AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY PR".split(" ");

const statusCopy: Record<OnboardingApplication["status"], { title: string; tone: "info" | "warning" | "danger" | "success"; body: string }> = {
  draft: { title: "Tell us about yourself", tone: "info", body: "We're required to verify everyone who opens an account. Your information is encrypted and shared only with our banking partner." },
  submitted: { title: "Submitted", tone: "info", body: "We've sent your application to our banking partner. This usually takes a few minutes." },
  in_review: { title: "In review", tone: "info", body: "Your application is being reviewed. We'll email you as soon as there's an update." },
  documents_required: { title: "Documents needed", tone: "warning", body: "Our partner needs a few documents to finish verifying your account." },
  approved: { title: "You're verified", tone: "success", body: "Your account is open. Your deposit details are on the overview page." },
  rejected: { title: "We couldn't verify this account", tone: "danger", body: "Contact support@payezy.io if you have questions." },
};

function errorsOf(e: unknown): Errors {
  if (!isProblem(e) || !e.details) return {};
  return Object.fromEntries(Object.entries(e.details).map(([k, v]) => [k, String(v)]));
}

const emptyAddress = (): Address => ({ street_line_1: "", street_line_2: "", city: "", state: "", zip: "" });
const emptyPerson = (): Person => ({ first_name: "", last_name: "", date_of_birth: "", email: "", phone: "", ssn: "", address: emptyAddress() });

function AddressFields({ value, onChange, path, errors, disabled }: { value: Address; onChange: (a: Address) => void; path: string; errors: Errors; disabled: boolean }) {
  const set = (k: keyof Address) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid gap-4 sm:grid-cols-6">
      <Field label="Street address" className="sm:col-span-4" error={errors[`${path}.street_line_1`]}>
        <Input value={value.street_line_1} onChange={set("street_line_1")} disabled={disabled} autoComplete="address-line1" />
      </Field>
      <Field label="Apt / suite" className="sm:col-span-2">
        <Input value={value.street_line_2 ?? ""} onChange={set("street_line_2")} disabled={disabled} autoComplete="address-line2" />
      </Field>
      <Field label="City" className="sm:col-span-3" error={errors[`${path}.city`]}>
        <Input value={value.city} onChange={set("city")} disabled={disabled} autoComplete="address-level2" />
      </Field>
      <Field label="State" className="sm:col-span-1" error={errors[`${path}.state`]}>
        <Select value={value.state} onChange={set("state")} disabled={disabled}>
          <option value="" />
          {STATES.map((s) => <option key={s}>{s}</option>)}
        </Select>
      </Field>
      <Field label="ZIP" className="sm:col-span-2" error={errors[`${path}.zip`]}>
        <Input value={value.zip} onChange={set("zip")} disabled={disabled} inputMode="numeric" autoComplete="postal-code" />
      </Field>
    </div>
  );
}

function PersonFields({ value, onChange, path, errors, disabled, title, ownership }: {
  value: Person; onChange: (p: Person) => void; path: string; errors: Errors; disabled: boolean; title?: boolean; ownership?: boolean;
}) {
  const set = (k: keyof Person) => (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Legal first name" error={errors[`${path}.first_name`]}>
          <Input value={value.first_name ?? ""} onChange={set("first_name")} disabled={disabled} autoComplete="given-name" />
        </Field>
        <Field label="Legal last name" error={errors[`${path}.last_name`]}>
          <Input value={value.last_name ?? ""} onChange={set("last_name")} disabled={disabled} autoComplete="family-name" />
        </Field>
        <Field label="Date of birth" error={errors[`${path}.date_of_birth`]}>
          <Input type="date" value={value.date_of_birth ?? ""} onChange={set("date_of_birth")} disabled={disabled} autoComplete="bday" />
        </Field>
        <Field label="Social Security number" hint="Sent securely to our banking partner; we keep only the last 4 digits visible." error={errors[`${path}.ssn`]}>
          <Input value={value.ssn ?? ""} onChange={set("ssn")} disabled={disabled} inputMode="numeric" autoComplete="off" placeholder="123-45-6789" />
        </Field>
        <Field label="Email" error={errors[`${path}.email`]}>
          <Input type="email" value={value.email ?? ""} onChange={set("email")} disabled={disabled} autoComplete="email" />
        </Field>
        <Field label="Mobile phone" error={errors[`${path}.phone`]}>
          <Input type="tel" value={value.phone ?? ""} onChange={set("phone")} disabled={disabled} autoComplete="tel" placeholder="(415) 555-0100" />
        </Field>
        {title && (
          <Field label="Title" error={errors[`${path}.title`]}>
            <Input value={value.title ?? ""} onChange={set("title")} disabled={disabled} placeholder="CEO" />
          </Field>
        )}
        {ownership && (
          <Field label="Ownership %" error={errors[`${path}.ownership_percentage`]}>
            <Input value={value.ownership_percentage ?? ""} onChange={set("ownership_percentage")} disabled={disabled} inputMode="decimal" />
          </Field>
        )}
      </div>
      <AddressFields value={value.address ?? emptyAddress()} onChange={(address) => onChange({ ...value, address })} path={`${path}.address`} errors={errors} disabled={disabled} />
    </div>
  );
}

function BusinessFields({ value, onChange, errors, disabled }: { value: Business; onChange: (b: Business) => void; errors: Errors; disabled: boolean }) {
  const set = (k: keyof Business) => (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Legal business name" error={errors["business.legal_name"]}>
          <Input value={value.legal_name ?? ""} onChange={set("legal_name")} disabled={disabled} autoComplete="organization" />
        </Field>
        <Field label="EIN" error={errors["business.ein"]}>
          <Input value={value.ein ?? ""} onChange={set("ein")} disabled={disabled} inputMode="numeric" placeholder="12-3456789" />
        </Field>
        <Field label="Business email" error={errors["business.email"]}>
          <Input type="email" value={value.email ?? ""} onChange={set("email")} disabled={disabled} />
        </Field>
        <Field label="Business phone" error={errors["business.phone"]}>
          <Input type="tel" value={value.phone ?? ""} onChange={set("phone")} disabled={disabled} />
        </Field>
        <Field label="Website" className="sm:col-span-2">
          <Input value={value.website ?? ""} onChange={set("website")} disabled={disabled} placeholder="https://" />
        </Field>
      </div>
      <AddressFields value={value.address ?? emptyAddress()} onChange={(address) => onChange({ ...value, address })} path="business.address" errors={errors} disabled={disabled} />
    </div>
  );
}

function Documents({ app, orgId, canEdit }: { app: OnboardingApplication; orgId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const path = { orgId };
  const fileRef = useRef<HTMLInputElement>(null);
  const kinds = app.required_documents.length ? app.required_documents : [{ kind: "supporting_document", display_name: "Supporting document", status: "optional" }];
  const [kind, setKind] = useState(kinds[0]!.kind);
  const refresh = () => qc.invalidateQueries({ queryKey: getOnboardingQueryKey({ path }) });
  const upload = useMutation({ ...uploadOnboardingDocumentMutation(), onSuccess: () => { toast.success("Uploaded"); void refresh(); }, onError: (e) => toast.error(problemMessage(e)) });
  const send = useMutation({ ...submitOnboardingDocumentsMutation(), onSuccess: () => { toast.success("Sent for review"); void refresh(); }, onError: (e) => toast.error(problemMessage(e)) });
  const pending = app.documents.filter((d) => d.status === "uploaded").length;

  return (
    <Card>
      <CardHeader title="Documents" description="PDF, PNG, or JPEG up to 10 MB." />
      <CardBody className="grid gap-4">
        {app.required_documents.length > 0 && (
          <ul className="grid gap-1 text-sm">
            {app.required_documents.map((d) => (
              <li key={d.kind + (d.person_label ?? "")} className="flex items-center gap-2">
                <Badge tone={d.status === "required" ? "warning" : "neutral"}>{humanize(d.status)}</Badge>
                {d.display_name}{d.person_label ? ` — ${d.person_label}` : ""}
              </li>
            ))}
          </ul>
        )}
        {app.documents.length > 0 && (
          <Table>
            <THead><TR><TH>File</TH><TH>Type</TH><TH>Status</TH><TH>Uploaded</TH></TR></THead>
            <tbody>
              {app.documents.map((d) => (
                <TR key={d.id}>
                  <TD>{d.file_name}</TD>
                  <TD>{humanize(d.kind)}</TD>
                  <TD><Badge tone={d.status === "failed" ? "danger" : d.status === "linked" ? "success" : "neutral"}>{d.status === "linked" ? "Sent" : humanize(d.status)}</Badge></TD>
                  <TD>{formatDate(d.created_at, true)}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
        {canEdit && (
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Document type">
              <Select value={kind} onChange={(e) => setKind(e.target.value)}>
                {kinds.map((d) => <option key={d.kind} value={d.kind}>{d.display_name}</option>)}
              </Select>
            </Field>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/png,image/jpeg"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload.mutate({ path, body: { file, kind } });
                e.target.value = "";
              }}
            />
            <Button variant="secondary" loading={upload.isPending} onClick={() => fileRef.current?.click()}>
              <FileUp className="size-4" /> Upload file
            </Button>
            {app.status === "documents_required" && (
              <Button loading={send.isPending} disabled={pending === 0} onClick={() => send.mutate({ path })}>Send for review</Button>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export function VerificationPage() {
  const org = useOrg();
  const qc = useQueryClient();
  const path = { orgId: org.org_id };
  const inFlight = (s?: string) => s === "submitted" || s === "in_review";
  const q = useQuery({ ...getOnboardingOptions({ path }), refetchInterval: (query) => (inFlight(query.state.data?.status) ? 4000 : false) });
  const app = q.data;
  const [data, setData] = useState<OnboardingData>({});
  const [accepted, setAccepted] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const prevStatus = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!app) return;
    if (prevStatus.current && prevStatus.current !== app.status) void qc.invalidateQueries({ queryKey: getMeQueryKey() });
    prevStatus.current = app.status;
    if (app.status === "draft") {
      setData({
        individual: app.entity_type === "individual" ? { ...emptyPerson(), ...app.data.individual } : undefined,
        business: app.entity_type === "business" ? { address: emptyAddress(), ...app.data.business } : undefined,
        controller: app.entity_type === "business" ? { ...emptyPerson(), ...app.data.controller } : undefined,
        beneficial_owners: app.data.beneficial_owners ?? [],
      });
    }
  }, [app, qc]);

  const onSaved = (a: OnboardingApplication) => qc.setQueryData(getOnboardingQueryKey({ path }), a);
  const save = useMutation({
    ...saveOnboardingMutation(),
    onSuccess: (a) => { setErrors({}); onSaved(a); toast.success("Draft saved"); },
    onError: (e) => { setErrors(errorsOf(e)); toast.error(problemMessage(e)); },
  });
  const submit = useMutation({
    ...submitOnboardingMutation(),
    onSuccess: (a) => { setErrors({}); onSaved(a); void qc.invalidateQueries({ queryKey: getMeQueryKey() }); },
    onError: (e) => { setErrors(errorsOf(e)); toast.error(problemMessage(e)); },
  });

  if (!app) {
    return (
      <>
        <PageHeader title="Verification" />
        {q.isError ? <Alert tone="danger">{problemMessage(q.error)}</Alert> : <Skeleton className="h-64" />}
      </>
    );
  }

  const copy = statusCopy[app.status];
  const editable = app.status === "draft" && org.can("onboarding:manage");
  const owners = data.beneficial_owners ?? [];
  const setOwner = (i: number, p: Person) => setData({ ...data, beneficial_owners: owners.map((o, j) => (j === i ? p : o)) });

  return (
    <>
      <PageHeader title="Verification" description={app.entity_type === "business" ? "Know Your Business (KYB)" : "Identity verification (KYC)"} />
      <div className="grid gap-6">
        <Alert tone={copy.tone} title={app.status === "draft" && app.entity_type === "business" ? "Tell us about your business" : copy.title}>
          {copy.body}
          {app.decision_reason && app.status === "draft" && <div className="mt-1">Our partner asked for changes: {app.decision_reason}</div>}
          {app.status === "approved" && (
            <div className="mt-2">
              <Link to="/o/$orgId" params={{ orgId: org.org_id }} className="font-medium underline">Go to your account</Link>
            </div>
          )}
        </Alert>

        {app.status === "draft" && (
          <form
            className="grid gap-6"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate({ path, body: data });
            }}
          >
            {app.entity_type === "individual" && data.individual && (
              <Card>
                <CardHeader title="Your details" description="Use your legal name and residential address in the United States." />
                <CardBody>
                  <PersonFields value={data.individual} onChange={(individual) => setData({ ...data, individual })} path="individual" errors={errors} disabled={!editable} />
                </CardBody>
              </Card>
            )}
            {app.entity_type === "business" && data.business && data.controller && (
              <>
                <Card>
                  <CardHeader title="Business" description="US-registered businesses only." />
                  <CardBody>
                    <BusinessFields value={data.business} onChange={(business) => setData({ ...data, business })} errors={errors} disabled={!editable} />
                  </CardBody>
                </Card>
                <Card>
                  <CardHeader title="Control person" description="An executive with significant responsibility for the business (e.g. CEO, CFO, managing member)." />
                  <CardBody>
                    <PersonFields value={data.controller} onChange={(controller) => setData({ ...data, controller })} path="controller" errors={errors} disabled={!editable} title />
                  </CardBody>
                </Card>
                <Card>
                  <CardHeader
                    title="Beneficial owners"
                    description="Everyone who directly or indirectly owns 25% or more."
                    actions={editable && owners.length < 4 ? (
                      <Button type="button" size="sm" variant="secondary" onClick={() => setData({ ...data, beneficial_owners: [...owners, { ...emptyPerson(), ownership_percentage: "25" }] })}>
                        <Plus className="size-4" /> Add owner
                      </Button>
                    ) : undefined}
                  />
                  <CardBody className="grid gap-8">
                    {errors["beneficial_owners"] && <Alert tone="danger">{errors["beneficial_owners"]}</Alert>}
                    {owners.length === 0 && <p className="text-sm text-ink-500">No one owns 25% or more.</p>}
                    {owners.map((o, i) => (
                      <div key={i} className="grid gap-3">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold">Owner {i + 1}</h4>
                          {editable && (
                            <Button type="button" size="sm" variant="ghost" onClick={() => setData({ ...data, beneficial_owners: owners.filter((_, j) => j !== i) })}>
                              <Trash2 className="size-4" /> Remove
                            </Button>
                          )}
                        </div>
                        <PersonFields value={o} onChange={(p) => setOwner(i, p)} path={`beneficial_owners[${i}]`} errors={errors} disabled={!editable} ownership />
                      </div>
                    ))}
                  </CardBody>
                </Card>
              </>
            )}
            {editable && (
              <div className="flex justify-end">
                <Button type="submit" variant="secondary" loading={save.isPending}>Save draft</Button>
              </div>
            )}
          </form>
        )}

        {app.status === "draft" && <Documents app={app} orgId={org.org_id} canEdit={editable} />}

        {app.status === "draft" && editable && app.terms && (
          <Card>
            <CardHeader title="Review and submit" />
            <CardBody className="grid gap-4">
              <label className="flex items-start gap-3 text-sm">
                <input type="checkbox" className="mt-0.5 size-4" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
                <span>
                  I agree to the{" "}
                  <a href={app.terms.url} target="_blank" rel="noreferrer" className="font-medium underline">{app.terms.title}</a>{" "}
                  (version {app.terms.version}) and confirm the information above is accurate. Payezy is not a bank; accounts are provided by our partner.
                </span>
              </label>
              <div>
                <Button
                  disabled={!accepted || save.isPending}
                  loading={submit.isPending}
                  onClick={async () => {
                    try {
                      await save.mutateAsync({ path, body: data });
                    } catch {
                      return;
                    }
                    submit.mutate({ path, body: { accept_terms: true, terms_version: app.terms!.version } });
                  }}
                >
                  Submit for verification
                </Button>
              </div>
            </CardBody>
          </Card>
        )}

        {(app.status === "documents_required" || app.status === "in_review" || app.status === "submitted") && (
          <Documents app={app} orgId={org.org_id} canEdit={org.can("onboarding:manage") && app.status !== "submitted"} />
        )}

        {app.status === "approved" && (
          <Card>
            <CardBody className="flex items-center gap-3 text-sm">
              <CheckCircle2 className="size-5 text-emerald-600" />
              Verified {app.submitted_at ? `· submitted ${formatDate(app.submitted_at)}` : ""}
            </CardBody>
          </Card>
        )}
      </div>
    </>
  );
}
